export interface ReportMetric {
  key: string;
  label: string;
  group: string;
  unit: string;
  decimals: number;
  direction: 'higher' | 'lower' | 'neutral';
  baseline?: number;
}
export interface ReportCell {
  current: number | null;
  previous?: number | null;
  note?: string;
}
export interface ReportRow {
  id: string;
  label: string;
  note?: string;
  values: Record<string, ReportCell>;
}
export interface AnalysisReport {
  type: 'analysis_report';
  id: string;
  title: string;
  context: string;
  notes: string[];
  metrics: ReportMetric[];
  rows: ReportRow[];
}
export type ReportView = 'current' | 'previous' | 'change' | 'relative' | 'baseline';
export type ReportStatus = 'all' | 'available' | 'missing';
export interface ReportLabels {
  name: string;
  current: string;
  previous: string;
  change: string;
  relative: string;
  baseline: string;
  details: string;
}
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const text = (value: unknown, max: number): value is string =>
  typeof value === 'string' && !!value.trim() && value.length <= max;
const identifier = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(value);
const number = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);
const nullable = (value: unknown) => value === null || number(value);
const keys = (value: Record<string, unknown>, allowed: string[]) =>
  Object.keys(value).every((key) => allowed.includes(key));

export function parseAnalysisReport(raw: unknown): AnalysisReport | undefined {
  try {
    const root: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (
      !object(root) ||
      root.version !== 1 ||
      !Array.isArray(root.widgets) ||
      root.widgets.length !== 1
    )
      return;
    const r: unknown = root.widgets[0];
    if (
      !object(r) ||
      r.type !== 'analysis_report' ||
      !identifier(r.id) ||
      !text(r.title, 200) ||
      !text(r.context, 4000) ||
      !keys(r, ['type', 'id', 'title', 'context', 'notes', 'metrics', 'rows']) ||
      !Array.isArray(r.notes) ||
      r.notes.length > 30 ||
      !r.notes.every((n) => text(n, 1000)) ||
      !Array.isArray(r.metrics) ||
      r.metrics.length < 1 ||
      r.metrics.length > 40 ||
      !Array.isArray(r.rows) ||
      r.rows.length > 1000
    )
      return;
    const metricKeys = new Set<string>();
    for (const m of r.metrics) {
      if (
        !object(m) ||
        !keys(m, ['key', 'label', 'group', 'unit', 'decimals', 'direction', 'baseline']) ||
        !identifier(m.key) ||
        metricKeys.has(m.key) ||
        !text(m.label, 120) ||
        !text(m.group, 80) ||
        typeof m.unit !== 'string' ||
        m.unit.length > 24 ||
        !Number.isInteger(m.decimals) ||
        (m.decimals as number) < 0 ||
        (m.decimals as number) > 4 ||
        !['higher', 'lower', 'neutral'].includes(String(m.direction)) ||
        ('baseline' in m && !number(m.baseline))
      )
        return;
      metricKeys.add(m.key);
    }
    const ids = new Set<string>();
    for (const row of r.rows) {
      if (
        !object(row) ||
        !keys(row, ['id', 'label', 'note', 'values']) ||
        !identifier(row.id) ||
        ids.has(row.id) ||
        !text(row.label, 200) ||
        ('note' in row && !text(row.note, 2000)) ||
        !object(row.values) ||
        Object.keys(row.values).length !== metricKeys.size ||
        !Object.keys(row.values).every((key) => metricKeys.has(key))
      )
        return;
      ids.add(row.id);
      for (const cell of Object.values(row.values)) {
        if (
          !object(cell) ||
          !keys(cell, ['current', 'previous', 'note']) ||
          !('current' in cell) ||
          !nullable(cell.current) ||
          ('previous' in cell && !nullable(cell.previous)) ||
          ('note' in cell && !text(cell.note, 1000))
        )
          return;
      }
    }
    return r as unknown as AnalysisReport;
  } catch {
    return;
  }
}

export function reportValue(
  metric: ReportMetric,
  cell: ReportCell,
  view: ReportView,
): number | null {
  const current = cell.current;
  const previous = cell.previous;
  let value: number | null | undefined;
  switch (view) {
    case 'current':
      value = current;
      break;
    case 'previous':
      value = previous;
      break;
    case 'baseline':
      value = current === null || metric.baseline === undefined ? null : current - metric.baseline;
      break;
    case 'change':
      value = current === null || previous == null ? null : current - previous;
      break;
    case 'relative':
      value =
        metric.unit === '%' || current === null || previous == null || previous <= 0
          ? null
          : ((current - previous) / previous) * 100;
      break;
  }
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function formatReportValue(
  metric: ReportMetric,
  value: number | null | undefined,
  view: ReportView = 'current',
  locale?: string,
): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const diff = view === 'change' || view === 'relative' || view === 'baseline';
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: view === 'relative' ? 1 : metric.decimals,
    maximumFractionDigits: view === 'relative' ? 1 : metric.decimals,
    signDisplay: diff ? 'exceptZero' : 'auto',
  }).format(Object.is(value, -0) ? 0 : value);
  return formatted + (view === 'relative' ? '%' : metric.unit === '%' ? (diff ? 'pp' : '%') : '');
}

/** Sort the complete filtered set before slicing a page; missing values always remain last. */
export function reportRows(
  report: AnalysisReport,
  search: string,
  status: ReportStatus,
  sort: { key: string; desc: boolean } | null,
  view: ReportView,
): ReportRow[] {
  const query = search.trim().toLocaleLowerCase();
  const rows = report.rows.filter(
    (row) =>
      (!query || row.label.toLocaleLowerCase().includes(query)) &&
      (status === 'all' ||
        (Object.values(row.values).some((cell) => cell.current !== null)
          ? status === 'available'
          : status === 'missing')),
  );
  const metric = report.metrics.find((item) => item.key === sort?.key);
  if (!sort || !metric) return rows;
  return [...rows].sort((a, b) => {
    const av = reportValue(metric, a.values[metric.key], view);
    const bv = reportValue(metric, b.values[metric.key], view);
    if (av === null) return bv === null ? 0 : 1;
    if (bv === null) return -1;
    return (av - bv) * (sort.desc ? -1 : 1);
  });
}

export function toggleReportSelection(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else if (next.size < 4) next.add(id);
  return next;
}

/** Generate only on copy/download; no second snapshot or hidden Markdown is stored. */
export function reportMarkdown(
  report: AnalysisReport,
  labels: ReportLabels,
  locale?: string,
): string {
  const escape = (value: string) =>
    value
      .replace(/\\/g, '\\\\')
      .replace(/\|/g, '\\|')
      .replace(/[\r\n]+/g, ' ')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  const lines = [
    '## ' + escape(labels.details) + ' · ' + escape(report.title),
    '',
    escape(report.context),
    '',
    ...report.notes.map((note) => '- ' + escape(note)),
    '',
  ];
  for (const row of report.rows) {
    lines.push(
      '### ' + escape(row.label),
      '',
      ...(row.note ? [escape(row.note), ''] : []),
      '| ' +
        [
          labels.name,
          labels.current,
          labels.previous,
          labels.change,
          labels.relative,
          labels.baseline,
          labels.details,
        ]
          .map(escape)
          .join(' | ') +
        ' |',
      '| :--- | ---: | ---: | ---: | ---: | ---: | :--- |',
    );
    for (const metric of report.metrics) {
      const cell = row.values[metric.key];
      const values = [
        metric.label + (metric.unit && metric.unit !== '%' ? ' · ' + metric.unit : ''),
        ...(['current', 'previous', 'change', 'relative'] as ReportView[]).map((view) =>
          formatReportValue(metric, reportValue(metric, cell, view), view, locale),
        ),
        formatReportValue(metric, metric.baseline, 'current', locale),
        cell.note ?? '',
      ];
      lines.push('| ' + values.map(escape).join(' | ') + ' |');
    }
    lines.push('');
  }
  return lines.join('\n');
}
